import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, ArrowLeft, Loader2, Lock, Stethoscope } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/useToast';
import { supabase } from '@/lib/supabase/client';
import { useTranslation } from 'react-i18next';

export default function ResetPasswordPage() {
  const { t } = useTranslation('doctor-platform');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tokens, setTokens] = useState<{
    accessToken: string | null;
    refreshToken: string | null;
  }>({
    accessToken: null,
    refreshToken: null
  });

  useEffect(() => {
    const validateToken = async () => {
      try {
        // First check if we already have a session (from Supabase /auth/v1/verify redirect)
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (session) {
          setTokens({
            accessToken: session.access_token,
            refreshToken: session.refresh_token
          });
          setIsTokenValid(true);
          return;
        }
        
        // If no session, try to get parameters from the hash (fallback)
        const hash = window.location.hash.substring(1); // Remove the # character
        const hashParams = new URLSearchParams(hash);
        
        // Also check query parameters (for custom implementations)
        const queryParams = new URLSearchParams(window.location.search);

        // Check for errors first
        const errorCode = hashParams.get('error_code') || queryParams.get('error_code');
        const errorDescription = hashParams.get('error_description') || queryParams.get('error_description');
        
        if (errorCode || errorDescription) {
          // Decode and format the error message
          const message = errorDescription ? decodeURIComponent(errorDescription.replace(/\+/g, ' ')) : t('auth.resetPassword.errors.invalidLink');
          setError(message);
          return;
        }

        // Try hash params first, then query params
        const accessToken = hashParams.get('access_token') || queryParams.get('access_token');
        const type = hashParams.get('type') || queryParams.get('type');
        const refreshToken = hashParams.get('refresh_token') || queryParams.get('refresh_token');
        
        // Check for token_hash in query params (from Supabase email link)
        const tokenHash = queryParams.get('token_hash');
        const token = queryParams.get('token');

        if (!accessToken && !token && !tokenHash) {
          setError(t('auth.resetPassword.errors.invalidLinkLong'));
          return;
        }
        
        if (type && type !== 'recovery') {
          setError(t('auth.resetPassword.errors.invalidLink'));
          return;
        }

        // If we have an access token, use it directly
        if (accessToken) {
          // Store the tokens
          setTokens({
            accessToken,
            refreshToken
          });

          // Try to verify the recovery token
          const authResponse = await supabase.auth.getUser(accessToken);

          if (authResponse?.error) {
            throw authResponse?.error;
          }

          // If we get here, the token is valid
          setIsTokenValid(true);
        } else if (token || tokenHash) {
          // If we only have a token or token_hash, try to exchange it
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash || token,
            type: 'recovery'
          });

          if (error) {
            throw error;
          }

          if (data?.session) {
            setTokens({
              accessToken: data.session.access_token,
              refreshToken: data.session.refresh_token
            });
            setIsTokenValid(true);
          } else {
            throw new Error(t('auth.resetPassword.errors.noSession'));
          }
        }
      } catch (error: any) {
        setError(t('auth.resetPassword.errors.invalidLinkLong'));
        setTimeout(() => {
          navigate('/auth/forgot-password');
        }, 3000);
      }
    };

    validateToken();
  }, [navigate, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Check if we have a valid session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session && !isTokenValid) {
      setError(t('auth.resetPassword.errors.invalidToken'));
      return;
    }

    // Password validation
    if (password.length < 8) {
      setError(t('auth.resetPassword.errors.length'));
      return;
    }

    if (!/(?=.*[a-z])/.test(password)) {
      setError(t('auth.resetPassword.errors.lowercase'));
      return;
    }

    if (!/(?=.*[A-Z])/.test(password)) {
      setError(t('auth.resetPassword.errors.uppercase'));
      return;
    }

    if (!/(?=.*\d)/.test(password)) {
      setError(t('auth.resetPassword.errors.number'));
      return;
    }

    if (!/(?=.*[@$!%*?&#])/.test(password)) {
      setError(t('auth.resetPassword.errors.special'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('auth.resetPassword.errors.mismatch'));
      return;
    }

    setIsLoading(true);

    try {
      // If we have tokens but no session, set the session first
      if (tokens.accessToken && !session) {
        await supabase.auth.setSession({
          access_token: tokens.accessToken,
          refresh_token: tokens.refreshToken || '',
        });
      }

      // Update the user's password using the active session
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) throw updateError;

      toast({
        title: t('auth.resetPassword.toast.title'),
        description: t('auth.resetPassword.toast.description'),
      });

      // Sign out and clear any existing sessions
      await supabase.auth.signOut();

      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      setTokens({ accessToken: null, refreshToken: null });

      // Redirect to login
      navigate('/auth/login');
    } catch (error: any) {
      setError(error.message || t('auth.resetPassword.errors.updateFailed'));
      setPassword('');
      setConfirmPassword('');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while validating token
  if (!isTokenValid && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center p-8 sm:p-12 lg:p-16 bg-white dark:bg-gray-900">
        <div className="mx-auto w-full max-w-md">
          <Button
            variant="ghost"
            className="mb-8"
            onClick={() => navigate("/auth/login")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('auth.resetPassword.backToLogin')}
          </Button>

          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Stethoscope className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">
              {t('auth.resetPassword.title')}
            </h1>
            <p className="text-muted-foreground">
              {t('auth.resetPassword.subtitle')}
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.resetPassword.newPassword')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9"
                  required
                  disabled={isLoading || !isTokenValid}
                  minLength={8}
                />
              </div>
              <div className="text-xs space-y-1 text-muted-foreground">
                <p>{t('auth.resetPassword.requirementsTitle')}</p>
                <ul className="list-disc pl-4">
                  <li>{t('auth.resetPassword.requirements.length')}</li>
                  <li>{t('auth.resetPassword.requirements.uppercase')}</li>
                  <li>{t('auth.resetPassword.requirements.lowercase')}</li>
                  <li>{t('auth.resetPassword.requirements.number')}</li>
                  <li>{t('auth.resetPassword.requirements.special')}</li>
                </ul>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t('auth.resetPassword.confirmPassword')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-9"
                  required
                  disabled={isLoading || !isTokenValid}
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || !isTokenValid}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('auth.resetPassword.submitting')}
                  </>
                ) : (
                t('auth.resetPassword.submit')
              )}
            </Button>
          </form>
        </div>
      </div>

      {/* Right side - Medical themed gradient */}
      <div className="hidden lg:block lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-500 to-teal-400">
          <div className="absolute inset-0 bg-black/20" />
        </div>
        <div className="relative h-full flex items-center justify-center p-16 text-white">
          <div className="max-w-xl text-center">
            <Stethoscope className="h-20 w-20 mx-auto mb-8 opacity-90" />
            <h2 className="text-4xl font-bold mb-6">{t('auth.resetPassword.heroTitle')}</h2>
            <p className="text-xl opacity-90 leading-relaxed">
              {t('auth.resetPassword.heroBody')}
            </p>
            <div className="mt-8 flex justify-center space-x-4">
              <div className="flex items-center">
                <div className="h-2 w-2 bg-white rounded-full mr-2" />
                <span className="text-sm opacity-75">{t('auth.resetPassword.compliance')}</span>
              </div>
              <div className="flex items-center">
                <div className="h-2 w-2 bg-white rounded-full mr-2" />
                <span className="text-sm opacity-75">{t('auth.resetPassword.encryption')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
